import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { set } from "date-fns";
import { toast } from "sonner";
import API_PATH from "~/utils/api-routes";
import { createSmkApi } from "~/utils/smkInterseptor";

interface ContactFormData {
  full_name: string;
  email: string;
  phone_number: string;
  subject: string;
  message: string;
}

interface ContactResponse {
  message: string;
  success: boolean;
  data?: any;
}

export interface ValueOfArtiType {
  office_hours: {
    end: string;
    start: string;
  };
  evening_darshan: {
    end: string;
    start: string;
  };
  morning_darshan: {
    end: string;
    start: string;
  };
}
export interface AartiTime {
  id: number;
  key: string;
  value: ValueOfArtiType;
}
interface ContactState {
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string;
  KundalAartiTime?: AartiTime | null;
  VadodaraAartiTime?: AartiTime | null;
}

const initialState: ContactState = {
  loading: false,
  error: null,
  success: false,
  message: "",
  KundalAartiTime: null,
  VadodaraAartiTime: null,
};

const smkService = createSmkApi();
export const submitContactFormThunk = createAsyncThunk("contact/submitForm", async (payload: ContactFormData, { rejectWithValue }) => {
  try {
    const response = await smkService.post<ContactResponse>(API_PATH.CONTACT_US, payload);

    if (response.status === 200 || response.status === 201) {
      return {
        data: response.data?.data,
        message: response.data?.message || "Message sent successfully!",
        success: true,
      };
    }
  } catch (error: any) {
    const axiosError = error as AxiosError<{ message: string }>;
    const message = axiosError.response?.data?.message || axiosError.message || "Failed to send message";
    return rejectWithValue(message);
  }
});

export const getKundalDhamArtiTime = createAsyncThunk("contact/getKundalDhamArtiTime", async () => {
  const res = await smkService.get(API_PATH.CONTACT_US_KUNDAL_ARTI_TIME);
  return res.data.data;
});

export const getVadodaraArtiTime = createAsyncThunk("contact/getVadodaraArtiTime", async () => {
  const res = await smkService.get(API_PATH.CONTACT_US_VADODARA_ARTI_TIME);
  return res.data.data;
});

const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    resetContactState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = "";
    },
    clearError: (state) => {
      state.error = null;
    },
    setKundalAartiTime: (state, action: PayloadAction<AartiTime>) => {
      state.KundalAartiTime = action.payload;
    },
    setVadodaraAartiTime: (state, action: PayloadAction<AartiTime>) => {
      state.VadodaraAartiTime = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Pending state
    builder.addCase(submitContactFormThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });

    // Fulfilled state
    builder.addCase(submitContactFormThunk.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.success = true;
      state.message = action.payload.message;
      state.error = null;
      toast.success(action.payload.message);
    });

    // Rejected state
    builder.addCase(submitContactFormThunk.rejected, (state, action) => {
      state.loading = false;
      state.success = false;
      state.error = action.payload as string;
      state.message = "";
      toast.error(action.payload as string);
    });

    // getArtiTime
    builder.addCase(getKundalDhamArtiTime.fulfilled, (state, action) => {
      state.KundalAartiTime = action.payload;
    });
    builder.addCase(getVadodaraArtiTime.fulfilled, (state, action) => {
      state.VadodaraAartiTime = action.payload;
    });
  },
});

export const { resetContactState, clearError, setKundalAartiTime, setVadodaraAartiTime } = contactSlice.actions;
export default contactSlice.reducer;
